import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Camera, Sparkles, Loader2, Image as ImageIcon } from "lucide-react";
import { sharePogWrapped } from "@/lib/pogWrappedCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PRESETS = [
  { id: "kaiju", label: "POGZILLA JR" },
  { id: "knight", label: "POG KNIGHT" },
  { id: "balloonist", label: "SKY PILOT" },
  { id: "alien", label: "UFO POG'R" },
  { id: "conductor", label: "CONDUCTOR" },
  { id: "royal", label: "POG ROYALTY" },
  { id: "dealdragon", label: "DEAL DRAGON" },
  { id: "sage", label: "SAVINGS SAGE" },
  { id: "monkey", label: "MONKEY POG" },
  { id: "robot", label: "DEAL BOT" },
  { id: "pirate", label: "LOOT PIRATE" },
  { id: "ghost", label: "NIGHT POGGER" },
];

const RINGS = {
  merchant: "linear-gradient(135deg,#fef3c7,#f59e0b 30%,#92400e 62%,#fbbf24)",
  consumer: "linear-gradient(135deg,#ddd6fe,#8b5cf6 30%,#4c1d95 62%,#a78bfa)",
};

const TIER_RINGS = {
  hatchling: "linear-gradient(135deg,#f1f5f9,#cbd5e1 35%,#64748b 65%,#e2e8f0)",
  stomper: "linear-gradient(135deg,#bbf7d0,#22c55e 35%,#14532d 65%,#4ade80)",
  crusher: "linear-gradient(135deg,#99f6e4,#14b8a6 35%,#134e4a 65%,#2dd4bf)",
  bolt: "linear-gradient(135deg,#fef9c3,#facc15 30%,#0891b2 65%,#fde047)",
  titan: "linear-gradient(135deg,#fef3c7,#f59e0b 30%,#92400e 62%,#fbbf24)",
};

const FLAIR_EMOJI = { crown: "👑", flame: "🔥", medal: "🏅", book: "📚" };

export const IdentityBadge = ({ uid, name = "P", size = 36, merchant = false, live = false, ver, onClick, className = "", testid, showFlair = false }) => {
  const [failed, setFailed] = useState(false);
  const [flairs, setFlairs] = useState([]);
  const [tier, setTier] = useState(null);
  useEffect(() => { setFailed(false); }, [uid, ver]);
  useEffect(() => {
    if (!showFlair || !uid) return;
    let ok = true;
    axios.get(`${API}/badge-flair/${encodeURIComponent(uid)}`).then((r) => { if (ok) { setFlairs(r.data.flairs || []); setTier(r.data.tier || null); } }).catch(() => {});
    return () => { ok = false; };
  }, [showFlair, uid]);
  const pad = Math.max(2, Math.round(size * 0.085));
  const initial = ((name || "P").replace(/[^a-zA-Z0-9]/g, "")[0] || "P").toUpperCase();
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`relative shrink-0 rounded-full ${onClick ? "active:scale-90 transition-transform" : ""} ${className}`}
      style={{ width: size, height: size, padding: pad,
        background: live || tier === "legend" ? undefined
          : (!merchant && tier && TIER_RINGS[tier]) || RINGS[merchant ? "merchant" : "consumer"],
        boxShadow: `0 0 ${Math.round(size / 4.5)}px rgba(${live ? "244,63,94" : tier === "legend" ? "217,70,239" : merchant || tier === "titan" ? "245,158,11" : "139,92,246"},0.5)` }}>
      {live && <span className="absolute inset-0 rounded-full pog-badge-live-ring" aria-hidden="true" />}
      {!live && tier === "legend" && <span className="absolute inset-0 rounded-full pog-badge-legend-ring" aria-hidden="true" />}
      <span className="relative block w-full h-full rounded-full overflow-hidden bg-slate-900 z-[1]">
        {!failed && uid ? (
          <img src={`${API}/badge/${encodeURIComponent(uid)}${ver ? `?v=${ver}` : ""}`} alt=""
            className="w-full h-full object-cover" draggable={false} onError={() => setFailed(true)} />
        ) : (
          <span className="w-full h-full flex items-center justify-center font-heading font-black text-slate-900"
            style={{ fontSize: Math.round((size - pad * 2) * 0.46), background: "linear-gradient(135deg,#e879f9,#fbbf24)" }}>
            {initial}
          </span>
        )}
      </span>
      {showFlair && flairs.length > 0 && (
        <span data-testid="badge-flair" className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-[2] flex gap-px leading-none pointer-events-none">
          {flairs.slice(0, 3).map((f) => (
            <span key={f} className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" style={{ fontSize: Math.max(11, Math.round(size * 0.24)) }}>{FLAIR_EMOJI[f]}</span>
          ))}
        </span>
      )}
    </Tag>
  );
};

export const RankUpSplash = ({ rank, onClose }) => {
  useEffect(() => {
    if (!rank) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [rank, onClose]);
  if (!rank) return null;
  const t = rank.rank.tier;
  return (
    <div data-testid="rank-up-splash" onClick={onClose}
      className="fixed inset-0 z-[2400] flex flex-col items-center justify-center bg-black/75 backdrop-blur-md cursor-pointer">
      <span className="text-7xl animate-bounce drop-shadow-[0_0_30px_rgba(217,70,239,0.8)]">{t.emoji}</span>
      <p className="mt-4 font-heading font-black text-sm tracking-[0.3em] text-fuchsia-300">POGZILLA EVOLVED</p>
      <p className="mt-1 font-heading font-black text-4xl text-white drop-shadow-lg">YOU'RE A {t.name}!</p>
      <p className="mt-2 text-sm font-bold text-amber-300">"{rank.rank.level.reaction}"</p>
      <p className="mt-4 text-[11px] font-bold text-slate-400">{rank.rank.points.toLocaleString()} POG POINTS · minted at the POGZ R US FACTORY 🏭</p>
      <p className="mt-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">tap anywhere to keep POG'GING</p>
    </div>
  );
};

const squareCrop = (file, px = 512) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const s = Math.min(img.width, img.height);
    const c = document.createElement("canvas");
    c.width = c.height = Math.min(px, s);
    c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("crop failed"))), "image/jpeg", 0.87);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
  img.src = url;
});

export const BadgeStudio = ({ open, onClose, uid, name, merchant = false, bizAuth = false, onSaved, ghost = false }) => {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [forgesLeft, setForgesLeft] = useState(null);
  const [ver, setVer] = useState(() => Date.now());
  const [rank, setRank] = useState(null);
  const [pogName, setPogName] = useState(name || "");
  const [savingName, setSavingName] = useState(false);
  const [ghostOn, setGhostOn] = useState(!!ghost);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open || bizAuth || !uid) return;
    axios.get(`${API}/badge-rank/${encodeURIComponent(uid)}`).then((r) => setRank(r.data.tier ? r.data : null)).catch(() => {});
  }, [open, bizAuth, uid]);
  if (!open) return null;

  const cfg = bizAuth
    ? { headers: { Authorization: `Bearer ${localStorage.getItem("biz_token") || ""}` } }
    : { withCredentials: true };

  const saved = (msg) => {
    setVer(Date.now());
    try { localStorage.setItem("airpog_badge_ver", String(Date.now())); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event("airpog:badge-changed"));
    onSaved?.();
    toast.success(msg || "🪪 BADGE LOCKED IN!");
  };

  const postBlob = async (blob, kind) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "badge.jpg");
      fd.append("kind", kind);
      const r = await axios.post(`${API}/badge`, fd, cfg);
      saved(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Badge didn't stick — give it another POG!");
    }
    setBusy(false);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("image/")) { toast.error("Pick an image file! 📸"); return; }
    squareCrop(f).then((b) => postBlob(b, "upload")).catch(() => toast.error("Couldn't read that image 😵"));
  };

  const pickPreset = async (id) => {
    if (busy) return;
    try {
      const res = await fetch(`/badges/${id}.jpg`);
      const b = await res.blob();
      await postBlob(b, "preset");
    } catch (e) { toast.error("Avatar didn't load — try again!"); }
  };

  const forge = async () => {
    if (prompt.trim().length < 3) { toast("Describe your badge first! ✨"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/badge/forge`, { prompt: prompt.trim() }, cfg);
      setForgesLeft(r.data.forges_left);
      saved(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The forge sputtered — try again!");
    }
    setBusy(false);
  };

  const savePogName = async () => {
    const n = pogName.trim().slice(0, 24);
    if (!n) { toast("Give yourself a POG NAME first!"); return; }
    setSavingName(true);
    try {
      await axios.post(`${API}/consumer/privacy`, { pog_nickname: n }, { withCredentials: true });
      onSaved?.();
      toast.success(`🎩 You're now @${n} — no real names needed on the badge!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Name didn't stick — try again!");
    }
    setSavingName(false);
  };

  const toggleGhost = async () => {
    const next = !ghostOn;
    setGhostOn(next);
    try {
      await axios.post(`${API}/consumer/privacy`, { leaderboard_hidden: next }, { withCredentials: true });
      onSaved?.();
      toast.success(next ? "🕵️ GHOST MODE ON — you're hidden from public boards." : "👋 Back on the boards — the kingdom sees you!");
    } catch (e) {
      setGhostOn(!next);
      toast.error("Couldn't flip Ghost Mode — try again!");
    }
  };

  const TabBtn = ({ id, icon, label }) => (
    <button data-testid={`badge-tab-${id}`} onClick={() => setTab(id)}
      className={`flex-1 h-9 rounded-xl text-[10px] font-heading font-black tracking-wider flex items-center justify-center gap-1.5 transition-colors ${tab === id ? "bg-fuchsia-500/25 border border-fuchsia-400/60 text-fuchsia-200" : "bg-slate-800/70 border border-slate-700 text-slate-400"}`}>
      {icon}{label}
    </button>
  );

  return (
    <div data-testid="badge-studio" className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={() => !busy && onClose()}>
      <div className="w-full sm:max-w-md bg-slate-900 border border-slate-700 sm:rounded-3xl rounded-t-3xl p-5 pb-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-heading font-black text-base text-white">🪪 POG'RS IDENTITY BADGE</p>
          <button data-testid="badge-studio-close" onClick={onClose} disabled={busy}
            className="w-8 h-8 rounded-xl bg-slate-800 flex items-center justify-center"><X className="w-4 h-4 text-slate-300" /></button>
        </div>
        <p className="text-[11px] font-bold text-slate-500 mt-0.5">A round minted POG — forged at the POGZ R US FACTORY 🏭 Shown across PICS · FLICS · LIVE.</p>

        <div className="mt-4 flex justify-center">
          <IdentityBadge uid={uid} name={name} size={96} merchant={merchant} ver={ver} showFlair testid="badge-studio-preview" />
        </div>

        {rank && (
          <div data-testid="badge-rank-chip" className="mt-3 mx-auto max-w-[280px]">
            <p className="text-center font-heading font-black text-xs text-white tracking-wider">
              {rank.tier.emoji} {rank.tier.name} <span className="text-slate-500">·</span> {rank.level.id} {rank.level.name} <span className="text-slate-500">·</span> <span className="text-amber-300">{rank.points.toLocaleString()} pts</span>
            </p>
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 transition-all" style={{ width: `${rank.progress}%` }} />
            </div>
            <p className="mt-1 text-center text-[10px] font-bold text-slate-500">
              {rank.next ? `${rank.next.to_go.toLocaleString()} pts to ${rank.next.emoji} ${rank.next.name}` : "🔥 MYTHIC POGZILLA — max evolution reached!"}
              <span className="text-slate-600"> · "{rank.level.reaction}"</span>
            </p>
          </div>
        )}

        {!bizAuth && (
          <div data-testid="pog-name-editor" className="mt-3 flex items-center gap-2">
            <input data-testid="pog-name-input" value={pogName} onChange={(e) => setPogName(e.target.value)} maxLength={24}
              placeholder="Your POG NAME (e.g. MR.POG)"
              className="flex-1 h-10 rounded-2xl bg-slate-800 border border-slate-700 text-white text-sm font-black px-3 outline-none focus:border-amber-400/60" />
            <button data-testid="pog-name-save" onClick={savePogName} disabled={savingName}
              className="h-10 px-4 rounded-2xl bg-amber-500 text-slate-900 font-heading font-black text-xs tracking-wider active:scale-95 transition-transform disabled:opacity-60">
              {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "SAVE"}
            </button>
          </div>
        )}
        {!bizAuth && (
          <button data-testid="ghost-mode-toggle" onClick={toggleGhost}
            className="mt-2 w-full h-9 rounded-2xl border flex items-center justify-center gap-2 text-[11px] font-heading font-black tracking-wider transition-colors active:scale-[0.98]"
            style={ghostOn ? { background: "rgba(148,163,184,0.15)", borderColor: "rgba(148,163,184,0.5)", color: "#cbd5e1" } : { background: "rgba(30,41,59,0.6)", borderColor: "#334155", color: "#64748b" }}>
            🕵️ GHOST MODE {ghostOn ? "ON — hidden from public boards" : "OFF — shown on public boards"}
          </button>
        )}
        {!bizAuth && <p className="mt-1 text-[10px] font-bold text-slate-500 text-center">No real names needed — your POG NAME is what the kingdom sees. 🎭</p>}

        <div className="mt-4 flex gap-2">
          <TabBtn id="upload" icon={<Camera className="w-3.5 h-3.5" />} label="UPLOAD" />
          <TabBtn id="avatars" icon={<ImageIcon className="w-3.5 h-3.5" />} label="POG AVATARS" />
          <TabBtn id="forge" icon={<Sparkles className="w-3.5 h-3.5" />} label="AI FORGE" />
        </div>

        <div className="mt-4 min-h-[9rem]">
          {tab === "upload" && (
            <div className="text-center">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />
              <button data-testid="badge-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
                className="w-full h-12 rounded-2xl bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white font-heading font-black text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} PICK A PIC
              </button>
              <p className="text-[10px] font-bold text-slate-500 mt-2">Any image you choose — auto-cropped into the coin. Max 8MB.</p>
            </div>
          )}
          {tab === "avatars" && (
            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => (
                <button key={p.id} data-testid={`badge-preset-${p.id}`} disabled={busy} onClick={() => pickPreset(p.id)}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
                  <span className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-700">
                    <img src={`/badges/${p.id}.jpg`} alt={p.label} className="w-full h-full object-cover" draggable={false} />
                  </span>
                  <span className="text-[9px] font-black text-slate-400 tracking-wider">{p.label}</span>
                </button>
              ))}
            </div>
          )}
          {tab === "forge" && (
            <div>
              <textarea data-testid="badge-forge-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                maxLength={300} rows={2} placeholder="a golden dragon wearing sunglasses…"
                className="w-full rounded-2xl bg-slate-800 border border-slate-700 text-white text-sm font-semibold p-3 outline-none focus:border-fuchsia-400/60 resize-none" />
              <button data-testid="badge-forge-btn" disabled={busy} onClick={forge}
                className="mt-2 w-full h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-rose-500 text-white font-heading font-black text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "FORGING…" : "FORGE MY BADGE"}
              </button>
              <p className="text-[10px] font-bold text-slate-500 mt-2 text-center">
                Nano Banana paints it ✨{forgesLeft != null ? ` · ${forgesLeft} forge${forgesLeft === 1 ? "" : "s"} left today` : " · 5 forges a day"}
              </p>
            </div>
          )}
        </div>

        {!bizAuth && (
          <div className="mt-3 flex gap-2">
            <button data-testid="badge-crew-link" onClick={() => { onClose(); navigate("/crew"); }}
              className="flex-1 h-10 rounded-2xl bg-slate-800/80 border border-amber-400/30 text-amber-300 font-heading font-black text-xs tracking-wider flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
              🤝 MY POG CREW
            </button>
            <button data-testid="badge-pogz-collection-link" onClick={() => { onClose(); navigate("/collect"); }}
              className="flex-1 h-10 rounded-2xl bg-slate-800/80 border border-fuchsia-400/30 text-fuchsia-300 font-heading font-black text-xs tracking-wider flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
              💎 MY POGZ →
            </button>
          </div>
        )}
        {!bizAuth && (
          <button data-testid="badge-wrapped-btn" onClick={() => sharePogWrapped()}
            className="mt-2 w-full h-10 rounded-2xl bg-slate-800/80 border border-sky-400/30 text-sky-300 font-heading font-black text-xs tracking-wider flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
            📊 MY POG WRAPPED — share your month
          </button>
        )}
      </div>
    </div>
  );
};
